/**
 * Turning a Book or Draft into the handful of values the mapping review draws.
 *
 * ## Why this is a separate, Angular-free file
 *
 * It was not, and that is how three defects shipped together on 2026-10-26: the
 * whole facet grid rendered an em-dash in every row, on every card. The logic
 * lived inside the component, where it could not be exercised without a
 * browser, so nothing caught any of them. Each is now pinned by
 * `tests/MappingItemFacts.smoke.ts`.
 *
 * ## The one that is not obvious: `body` and `cover` are NOT nested
 *
 * A Book's geometry does not arrive with the Book. `BookService.getLinkData()`
 * declares:
 *
 * ```
 * { action: 'Link', objectIdPropertyName: 'body_ID',  serviceName: 'bodyService',  objectProperty: 'body'  }
 * { action: 'Link', objectIdPropertyName: 'cover_ID', serviceName: 'coverService', objectProperty: 'cover' }
 * ```
 *
 * — so `book.body` is populated by a *reverse link* from whatever
 * `BodyService` happens to be holding, and `GET /book/{id}` nests nothing.
 * `DraftService` does the same through `draftBodyService` / `draftCoverService`.
 *
 * The review screen never loads any of those four services. So `item.body` was
 * `undefined` for every card, and Interior PDF, Cover PDF, Pages and Trim Size
 * were structurally incapable of showing a value — not "missing data", a
 * missing *fetch*. `body_ID` and `cover_ID` are on the item, so the caller can
 * resolve them; this function deliberately takes the two side objects as
 * arguments rather than reaching into the item, so the dependency is explicit
 * and the function stays pure.
 *
 * **This is also why `display` (contract §3.3) matters more than it first
 * looked.** Inline properties are not an optimisation here — without them the
 * client needs up to *three* round trips per card (item, body, cover) to fill
 * one grid.
 *
 * No Angular import: loaded by the smoke test under plain ts-node, same
 * arrangement as `mapping-facets.ts`.
 */

import {ProjectMappingItemDisplay} from '../../../model/ProjectMapping';
import {MappingRecencyNote} from './mapping-facets';

/**
 * Everything the grid draws, normalised away from the two source shapes.
 *
 * Numbers are raw, not formatted: localizing digits needs `LanguagePipe` and
 * therefore Angular, so it stays in the component. `0` and `''` both mean "not
 * known", which is what makes the row render a dash.
 */
export interface MappingItemFacts {
  title: string;
  author: string;
  bodyResource_ID: number;
  coverResource_ID: number;
  numPages: number;
  /** Points, as Body/DraftBody store it. */
  trimWidth: number;
  trimHeight: number;
  isbn: string;
  work_ID: number;
  /** Raw code, e.g. `PERFECT` / `COIL`. Translated by the caller. */
  bindingType: string;
  /** Interior paper. Resolved to a name by the caller via PaperTypeService. */
  paperType_ID: number;
  /** Raw code, e.g. `GLOSS` / `MATTE`. Translated by the caller. */
  laminateType: string;
  /** Epoch ms, or 0 when unknown. */
  lastUpdateMs: number;
}

const EMPTY: MappingItemFacts = {
  title: '', author: '',
  bodyResource_ID: 0, coverResource_ID: 0,
  numPages: 0, trimWidth: 0, trimHeight: 0,
  isbn: '', work_ID: 0,
  bindingType: '', paperType_ID: 0, laminateType: '',
  lastUpdateMs: 0
};

/**
 * Parse a `lastUpdate` into epoch ms, or 0.
 *
 * Three shapes reach this: an ISO string from `display`, a `Date` from the
 * cache, and a number.
 *
 * **`new Date(0)` must read as unknown, not as 1 Jan 1970.** `Book`'s
 * constructor defaults `lastUpdate` to exactly that, so a Book that has never
 * been given a real date would otherwise be the oldest item in every group it
 * appears in and would drag every sibling's "days older" count to ~20,000.
 */
export function toMillis(value: any): number {
  if ( value === null || value === undefined || value === '' ) {
    return 0;
  }
  const ms = value instanceof Date ? value.getTime() : new Date(value).getTime();
  if ( isNaN(ms) || ms <= 0 ) {
    return 0;
  }
  return ms;
}

/** Which side rows an item points at, so the caller knows what to fetch. */
export function sideIds(item: any): { body_ID: number, cover_ID: number } {
  if ( !item ) {
    return { body_ID: 0, cover_ID: 0 };
  }
  return { body_ID: item.body_ID || 0, cover_ID: item.cover_ID || 0 };
}

/** Facts from the server's inline block. */
export function factsFromDisplay(display: ProjectMappingItemDisplay): MappingItemFacts {
  if ( !display ) {
    return Object.assign({}, EMPTY);
  }
  return {
    title: display.title || '',
    author: display.author || '',
    bodyResource_ID: display.bodyResource_ID || 0,
    coverResource_ID: display.coverResource_ID || 0,
    numPages: display.numPages || 0,
    trimWidth: display.trimWidth || 0,
    trimHeight: display.trimHeight || 0,
    isbn: display.isbn || '',
    work_ID: display.work_ID || 0,
    bindingType: display.bindingType || '',
    paperType_ID: display.paperType_ID || 0,
    laminateType: display.laminateType || '',
    lastUpdateMs: toMillis(display.lastUpdate)
  };
}

/**
 * Facts from a cached Book or Draft plus its separately-resolved side rows.
 *
 * `body` / `cover` are passed in because they are NOT on the item — see the
 * file comment. Passing `null` for either is legitimate and yields zeros for
 * that side's rows; it is the honest answer while the fetch is in flight.
 *
 * Drafts carry no identifier at all, which is why `isbn` falls back to `sfbn`
 * and then to empty rather than being read off a field Drafts do not have.
 */
export function factsFromItem(item: any, body: any, cover: any): MappingItemFacts {
  if ( !item ) {
    return Object.assign({}, EMPTY);
  }
  return {
    title: item.title || '',
    author: item.author || '',
    bodyResource_ID: (body && body.resource_ID) || 0,
    coverResource_ID: (cover && cover.resource_ID) || 0,
    numPages: (body && body.numPages) || 0,
    trimWidth: (body && body.width) || 0,
    trimHeight: (body && body.height) || 0,
    isbn: item.isbn || item.sfbn || '',
    work_ID: item.work_ID || 0,
    // Binding is on the ITEM; paper is on the body row and lamination on the
    // cover row, which is why both sides have to be resolved before any of
    // this grid can be filled.
    bindingType: item.bindingType || '',
    paperType_ID: (body && body.paperType_ID) || 0,
    laminateType: (cover && cover.laminateType) || '',
    lastUpdateMs: toMillis(item.lastUpdate)
  };
}

/**
 * How one card's date relates to the newest item in its group.
 *
 * Returns `null` when either date is unknown, which leaves the row blank
 * rather than claiming a comparison that was never made.
 *
 * Days are rounded, so two items edited a few hours apart read as the same day
 * rather than as "1 day older" in whichever direction the clock fell.
 */
export function recencyNote(cardMs: number, newestMs: number): { kind: MappingRecencyNote, days: number } {
  if ( !cardMs || !newestMs ) {
    return null;
  }
  const days = Math.round((newestMs - cardMs) / (24 * 60 * 60 * 1000));
  if ( days === 0 ) {
    return { kind: 'MOST_RECENT', days: 0 };
  }
  return { kind: days > 0 ? 'DAYS_OLDER' : 'DAYS_NEWER', days: Math.abs(days) };
}
